import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    WeezHeader,
    Button,
    Icon,
    Card,
    ContentCard,
    Tooltip,
    FilterSetController,
    Filter,
    SelectButton,
    Dropdown,
    Table,
    Paginator,
    TableCellDropdown,
    Toggle,
    ToastContext,
    InfoText
} from '@weezevent/nacre';
import DropdownPageSize from '../../manager/components/dropdownPageSize';
import WaitingListModel from '../../../models/waitingListModel';
import EventModel from '../../../models/eventModel';
import { useFilters, handleSearchQuery } from '../../manager/components/context';
import LoadingTable from '../../../components/loadingTable';
import SortDropdown from '../../../components/sortDropdown';
import Style from './list.module.css';
import DeactivationModal from './deactivationModal';
import DeleteModal from './deleteModal';
import { InfoTextTooltip } from '../../attendee/detail/components/infotext-tooltip/infoTextTooltip';
import { RowDate } from '../../design/list/components/template-row/templateRow';

function HeaderRow({ active = false, ordering, onChangeSort = () => {} }) {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader active={active} className={Style['tr-header']}>
            <SortDropdown
                className={Style['width-x-large']}
                name="name"
                ordering={ordering}
                label={t('ticket.waiting_list.tableHeader.name')}
                items={[
                    <Dropdown.Item.Icons key={1} item={t('ticket.common.ordering.A-Z')} checked={ordering === 'name'} onClick={() => onChangeSort('name')} />,
                    <Dropdown.Item.Icons key={2} item={t('ticket.common.ordering.Z-A')} checked={ordering === '-name'} onClick={() => onChangeSort('-name')} />
                ]}
            />
            <th className={`${Style['width-x-regular']}`}>{t('ticket.waiting_list.tableHeader.subscription_rate')}</th>
            <th className={`${Style['width-x-regular']}`}>{t('ticket.waiting_list.tableHeader.purchase_rate')}</th>
            <th className={`${Style['width-x-regular']}`}>{t('ticket.waiting_list.tableHeader.next_processing')}</th>
            <th className={`${Style['width-x-regular']}`}>{t('ticket.waiting_list.tableHeader.subscriptions')}</th>
            <th className={`${Style['width-x-mini']}`}>{t('ticket.waiting_list.tableHeader.activation')}</th>
            <th className={`${Style['width-x-mini']}`}>{t('ticket.waiting_list.tableHeader.actions')}</th>
        </Table.Tr>
    );
}

function WaitingListRow({
    waitingList,
    organization,
    onSelected = () => {},
    isChecked = false,
    setObjectToDelete = () => {},
    onToggle = () => {},
    loadingToggle = false,
    eventId = null
}) {
    const history = useHistory();
    const { t } = useTranslation();

    const ratesDisplay = React.useMemo(() => {
        if (!waitingList.rates.length) {
            return '-';
        }
        if (waitingList.rates.length === 1) {
            return <InfoTextTooltip title={waitingList.rates[0]?.name} subtitle={waitingList.rates[0]?.event.name} />;
        }
        return t('ticket.waiting_list.rate_quantity', { count: waitingList.rates.length });
    }, [t, waitingList]);

    const onClick = React.useCallback(
        e => {
            e.preventDefault();
            onToggle(waitingList);
        },
        [onToggle]
    );

    const toggleValue = React.useMemo(() => {
        return waitingList.status === 'active' ? true : false;
    }, [waitingList]);

    const onRowClick = React.useCallback(() => {
        if (eventId) {
            history.push(`waiting_lists/${waitingList.id}/subscribers`);
        } else {
            history.push(`waiting_lists/${waitingList.id}`);
        }
    }, [waitingList, eventId]);

    return (
        <Table.Tr active={isChecked} onSelected={(event, data) => onSelected({ event, data }, waitingList)} onClick={onRowClick}>
            <Table.Td className={`${Style['width-x-large']} ${Style['list__td-info-text']}`}>{waitingList.name}</Table.Td>
            <Table.Td className={`${Style['width-x-regular']} ${Style['list__td-info-text']}`}>{ratesDisplay}</Table.Td>
            <Table.Td className={`${Style['width-x-regular']} ${Style['list__td-info-text']}`}>
                <InfoTextTooltip title={waitingList.target_rate.name} subtitle={waitingList.target_rate.event.name} />
            </Table.Td>
            <Table.Td className={`${Style['width-x-regular']} ${Style['list__td-info-text']}`}>
                <RowDate date={waitingList.next_processing} organization={organization} />
            </Table.Td>
            <Table.Td className={`${Style['width-x-regular']} ${Style['list__td-info-text']}`} onClick={e => e.stopPropagation()}>
                <InfoText subtitle={t('ticket.waiting_list.waiting', { count: waitingList.pending_subscribers || 0 })}>
                    <div>
                        {waitingList.subscribers}
                        {!eventId && (
                            <Tooltip triggerOnHover title={t('ticket.waiting_list.external_link_tooltip')}>
                                <Icon
                                    name="external-link"
                                    onClick={() => history.push(`/ticket/O${organization.id}/E${waitingList.target_rate.event.id}/waiting_lists/${waitingList.id}/subscribers`)}
                                />
                            </Tooltip>
                        )}
                    </div>
                </InfoText>
            </Table.Td>
            <Table.Td className={`${Style['width-x-mini']} ${Style['list__td-info-text']}`} onClick={e => e.stopPropagation()}>
                <span onClick={onClick}>
                    <Toggle checked={toggleValue} disabled={loadingToggle} />
                </span>
            </Table.Td>
            <TableCellDropdown renderWhenClosed className={`${Style['width-x-mini']} ${Style['inventories-actions']}`}>
                <Dropdown.Item
                    item={
                        <div className={Style['edit-external-link']}>
                            {t('ticket.waiting_list.edit_list')}
                            {eventId && <Icon name="external-link" />}
                        </div>
                    }
                    onClick={() => history.push(`/ticket/O${organization.id}/waiting_lists/${waitingList.id}`)}
                />
                {eventId && <Dropdown.Item item={t('ticket.waiting_list.event.seeSubscribers')} onClick={() => history.push(`waiting_lists/${waitingList.id}/subscribers`)} />}
                <Dropdown.Line />
                <Dropdown.Item item={t('ticket.waiting_list.delete_list')} deleteItem onClick={() => setObjectToDelete(waitingList)} />
            </TableCellDropdown>
        </Table.Tr>
    );
}

const spec = {
    multiple: ['target_rate__event__in', 'status_in'],
    fields: ['search'],
    aliases: {}
};

export default function WaitingList({ organization, event }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [selectedLists, setSelectedLists] = React.useState({});
    const [objectToDelete, setObjectToDelete] = React.useState(null);
    const [objectToDeactivate, setObjectToDeactivate] = React.useState(null);
    const [ordering, setOrdering] = React.useState('name');
    const [currentPage, setCurrentPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(50);
    const [allSelected, setAllSelected] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [loadingToggle, setLoadingToggle] = React.useState(false);
    const [filters, setFilters] = useFilters(spec);

    const [waitingLists, loadingWaitingLists, syncWaitingLists] = WaitingListModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event?.id,
            cache: false,
            allow_cache: false,
            query: {
                ordering,
                expand: 'target_rate,rates',
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters, false)
            }
        },
        [organization, filters, ordering, pageSize, currentPage, event]
    );

    const [events] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            launch: Boolean(!event?.id),
            query: {
                time_status: 'ongoing, upcoming'
            }
        },
        [organization, event]
    );

    const eventOptions = React.useMemo(() => {
        if (!events) return [];
        return (
            events &&
            events.map(e => {
                return { key: e.id, text: e.name, name: e.id };
            })
        );
    }, [events]);

    const activationOptions = [
        {
            key: 'active',
            text: t('ticket.waiting_list.active'),
            name: 'active'
        },
        {
            key: 'inactive',
            text: t('ticket.waiting_list.inactive'),
            name: 'inactive'
        }
    ];

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    const resetSelected = React.useCallback(() => {
        setAllSelected(false);
        setSelectedLists({});
    });

    const countWaitingLists = React.useMemo(() => {
        return Number.parseInt(waitingLists?.__meta?.headers.get('total-count')) || 0;
    }, [waitingLists, loadingWaitingLists]);

    const onChangeSort = ordering => {
        setCurrentPage(0);
        setOrdering(ordering);
    };

    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, waitingList) => {
            event.stopPropagation();
            setSelectedLists(currentState => {
                const newSelected = { ...currentState };
                const isSelected = waitingList.id in currentState;
                if (!checked && isSelected) {
                    delete newSelected[waitingList.id];
                }

                if (checked && !isSelected) {
                    newSelected[waitingList.id] = waitingList;
                }
                setAllSelected(Object.values(newSelected).length === waitingLists.length);
                return newSelected;
            });
        },
        [waitingLists]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            if (checked) {
                setAllSelected(true);
                let waitingListsTmp = {};
                waitingLists.forEach(waitingList => {
                    waitingListsTmp[waitingList.id] = waitingList;
                });
                setSelectedLists(waitingListsTmp);
            } else {
                setAllSelected(false);
                setSelectedLists({});
            }
        },
        [waitingLists]
    );

    const onSubmitToggle = React.useCallback(
        waitingList => {
            if (loadingToggle) return;
            const _toggleState = waitingList.status === 'active' ? 'inactive' : 'active';

            setIsLoading(true);
            setLoadingToggle(true);
            const _waitingList = new WaitingListModel({
                id: waitingList.id,
                status: _toggleState
            });

            _waitingList
                .save({
                    organizationId: organization.id
                })
                .then(() => {
                    toast.success(t(`ticket.waiting_list.deactivation_modal.toast-success-${_toggleState}`));
                    setObjectToDeactivate(null);
                    setIsLoading(false);
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.waiting_list.deactivation_modal.toast-error'));
                })
                .finally(() => {
                    setLoadingToggle(false);
                    setIsLoading(false);
                    syncWaitingLists();
                });
        },
        [organization, loadingToggle, t, toast, syncWaitingLists]
    );

    const onToggle = React.useCallback(
        waitingList => {
            if (waitingList.status === 'active' && waitingList.subscribers > 0) {
                setObjectToDeactivate(waitingList);
            } else {
                setObjectToDeactivate(null);
                onSubmitToggle(waitingList);
            }
        },
        [onSubmitToggle]
    );

    return (
        <>
            <DeactivationModal
                waitingList={objectToDeactivate}
                onSubmit={() => onSubmitToggle(objectToDeactivate)}
                onClose={() => setObjectToDeactivate(null)}
                isLoading={isLoading}
            />
            <DeleteModal
                waitingList={objectToDelete}
                onClose={() => {
                    setObjectToDelete(null);
                }}
                organization={organization}
                sync={syncWaitingLists}
            />
            <WeezHeader
                title={t('ticket.waiting_list.title')}
                rightComponent={[
                    <Button key={0} onClick={() => history.push(`/ticket/O${organization.id}/waiting_lists/new`)} primary icon={<Icon name="plus" />}>
                        {t('ticket.waiting_list.create_new')}
                    </Button>
                ]}
            >
                {t('ticket.waiting_list.description')}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={countWaitingLists}
                        isLoadingTotal={loadingWaitingLists}
                        labelTotal={t('ticket.waiting_list.total', { count: countWaitingLists })}
                        labelReset={t('ticket.common.action.reset')}
                        search={{
                            onChange: (synth, value) => {
                                onChangeSearch(value);
                                resetSelected();
                            },
                            handleReset: (synth, value) => {
                                onChangeSearch(null);
                            },
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                        onChange={values => {
                            if (filters.search !== undefined && filters.search.length > 0) {
                                values.search = filters.search;
                            }
                            setFilters(values);
                            resetSelected();
                        }}
                    >
                        {!event?.id && (
                            <Filter
                                name="target_rate__event__in"
                                multiple
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelName={t('ticket.waiting_list.events')}
                                options={eventOptions}
                                index="0"
                                labelCTA={t('ticket.common.action.apply')}
                            />
                        )}
                        <Filter
                            name="status_in"
                            multiple
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.waiting_list.activation')}
                            options={activationOptions}
                            index="1"
                            labelCTA={t('ticket.common.action.apply')}
                        />
                    </FilterSetController>
                    <div>
                        <div className={Style['global-pagesize-container']}>
                            <SelectButton
                                tooltipTitle={t('ticket.waiting_list.noActionTooltip')}
                                title={
                                    Object.values(selectedLists).length > 0
                                        ? t('ticket.waiting_list.listsAction', { count: Object.values(selectedLists).length })
                                        : t('ticket.common.action.bulk')
                                }
                                objects={Object.values(selectedLists)}
                            >
                                <Dropdown.Item disabled={Object.values(selectedLists).length < 1} onClick={() => setObjectToDelete(selectedLists)} deleteItem>
                                    {t('ticket.common.action.delete')}
                                </Dropdown.Item>
                            </SelectButton>
                            <DropdownPageSize
                                loading={loadingWaitingLists}
                                current={pageSize}
                                onChange={setPageSize}
                                pageSizes={[10, 25, 50]}
                                onChangeCurrentpage={setCurrentPage}
                                totalCount={countWaitingLists || 0}
                            />
                        </div>
                    </div>
                    <Table useNew selectable onSelectAll={(event, data) => handleAllClick({ event, data })}>
                        <Table.THead>
                            <HeaderRow active={allSelected} ordering={ordering} onChangeSort={onChangeSort} />
                        </Table.THead>
                        <Table.TBody>
                            {waitingLists?.map((waitingList, index) => (
                                <WaitingListRow
                                    key={index}
                                    index={index}
                                    organization={organization}
                                    waitingList={waitingList}
                                    isChecked={Boolean(selectedLists[waitingList.id])}
                                    onSelected={handleElementsList}
                                    setObjectToDelete={setObjectToDelete}
                                    onToggle={onToggle}
                                    loadingToggle={loadingToggle}
                                    eventId={event?.id}
                                />
                            ))}
                        </Table.TBody>
                    </Table>
                    <LoadingTable loading={loadingWaitingLists} />
                    {!loadingWaitingLists && !waitingLists?.length && (
                        <Card.EmptyState
                            title={t('ticket.waiting_list.empty.title')}
                            subtitle={t('ticket.waiting_list.empty.description')}
                            icon={<Icon name="list" />}
                            labelButton={t('ticket.waiting_list.create_new')}
                            onClick={() => {
                                history.push('waiting_lists/new');
                            }}
                        />
                    )}
                    <div style={{ textAlign: 'center', margin: '10px 0' }}>
                        <Paginator
                            currentPage={currentPage}
                            onPageChange={numberPage => {
                                setCurrentPage(numberPage);
                                resetSelected();
                            }}
                            totalCount={countWaitingLists || 0}
                            pageSize={pageSize}
                        />
                    </div>
                </ContentCard>
            </Card>
        </>
    );
}
